import { lazy, Suspense, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { SERVICES, type ServiceSlug } from "@/lib/site";
import { FAQAccordion, type FAQItem } from "./FAQAccordion";
import { ServiceGallery, type ServiceGalleryItem } from "./ServiceGallery";
import { WhatsAppLink } from "./WhatsAppLink";
import { ContenidoMarkdown, ESTILO_ENLACES, TextoRico } from "./ContenidoMarkdown";
import type { VistaServicio } from "@/lib/page-content/vista";

const LeadForm = lazy(() => import("./LeadForm").then((m) => ({ default: m.LeadForm })));

/** Placeholder mientras carga el chunk lazy de LeadForm, para no dejar un
 * espacio en blanco en conexiones lentas. */
function LeadFormSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-xl animate-pulse rounded-lg bg-background p-6 shadow-pop md:max-w-none md:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
        <div className="h-12 rounded-md bg-border/60" />
      </div>
      <div className="mt-4 h-24 rounded-md bg-border/60" />
      <div className="mt-4 h-12 rounded-md bg-border/60" />
    </div>
  );
}

export interface ServicePageProps {
  /** Ruta de la página: un servicio de SERVICES o una landing que cuelga de uno (`servicio`). */
  slug: ServiceSlug | `/${string}`;
  /**
   * Servicio al que pertenece una landing que no está en SERVICES (p. ej. /canastas-navidenas-corporativas
   * → /merchandising-corporativo): lo usan el formulario, el WhatsApp y los relacionados. En una página
   * de servicio se omite.
   */
  servicio?: ServiceSlug;
  heroImg: string;
  heroImgWebp?: string;
  heroSrcset?: string;
  /** Texto de la página (editable desde el panel; specs/138). */
  vista: VistaServicio;
  relatedSlugs?: ServiceSlug[];
  gallery?: ServiceGalleryItem[];
}

export function ServicePage(props: ServicePageProps) {
  const v = props.vista;
  const p = { ...props, eyebrow: v.eyebrow, h1: v.h1, intro: v.intro };
  // El value del select del formulario es el `name` de SERVICES, no el h1 de la página
  const base = p.servicio ?? p.slug;
  const serviceName = SERVICES.find((s) => s.slug === base)?.name;
  const related = p.relatedSlugs
    ? (p.relatedSlugs
        .map((slug) => SERVICES.find((s) => s.slug === slug))
        .filter(Boolean) as (typeof SERVICES)[number][])
    : SERVICES.filter((s) => s.slug !== base).slice(0, 3);
  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
            <div className="hero-rise">
              <span className="accent-bar-diagonal inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-accent shadow-[0_0_8px_var(--accent)]" />
                {p.eyebrow}
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-secondary text-balance md:text-5xl lg:text-6xl">
                {p.h1}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-secondary/75 md:text-lg">
                {p.intro}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/cotizar"
                  className="btn-cta inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold"
                >
                  Solicitar propuesta →
                </Link>
                <WhatsAppLink
                  topic={serviceName}
                  topicId={p.slug.slice(1)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-6 py-3.5 text-sm font-semibold text-secondary hover:border-primary hover:text-primary"
                >
                  Hablar por WhatsApp
                </WhatsAppLink>
              </div>
            </div>
            <div className="relative hero-rise [animation-delay:120ms]">
              <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-primary/10 via-accent/10 to-transparent blur-2xl" />
              <div className="img-service relative overflow-hidden rounded-2xl shadow-soft">
                <picture>
                  {p.heroSrcset ? (
                    <source
                      srcSet={p.heroSrcset}
                      sizes="(min-width: 1024px) 45vw, 100vw"
                      type="image/webp"
                    />
                  ) : (
                    p.heroImgWebp && <source srcSet={p.heroImgWebp} type="image/webp" />
                  )}
                  <img
                    src={p.heroImg}
                    alt={p.h1}
                    width={800}
                    height={600}
                    loading="eager"
                    fetchPriority="high"
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPCIÓN */}
      {v.description.articulo && v.description.html ? (
        // Artículo: la nota va debajo, igual que en el diseño simple (si no, una nota escrita en el
        // panel no se veía). Listas con viñetas y números: el reset de Tailwind los quita.
        <section className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20">
          <ContenidoMarkdown
            html={v.description.html}
            className="text-base leading-relaxed text-secondary/75 md:text-lg [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-secondary [&_h2]:mt-12 [&_h2]:font-display [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:leading-tight [&_h2]:tracking-tight [&_h2]:text-secondary [&>h2:first-child]:mt-0 md:[&_h2]:text-4xl [&_h2+p]:mt-5 [&_p+p]:mt-4 [&_ul]:mt-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mt-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mt-3 [&_ul+p]:mt-5 [&_ol+p]:mt-5"
          />
          {v.note && (
            <div className={`mt-8 text-base text-secondary/70 ${ESTILO_ENLACES}`}>
              <TextoRico t={v.note} />
            </div>
          )}
        </section>
      ) : (
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-5 md:px-8">
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              ¿En qué consiste el servicio?
            </h2>
            <div className={`mt-5 text-lg text-secondary/75 [&_p+p]:mt-4 ${ESTILO_ENLACES}`}>
              {v.description.html ? (
                <ContenidoMarkdown html={v.description.html} />
              ) : (
                <p>{v.description.texto}</p>
              )}
            </div>
            {v.note && (
              <div className={`mt-4 text-base text-secondary/70 ${ESTILO_ENLACES}`}>
                <TextoRico t={v.note} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* INCLUYE + PARA QUIÉN */}
      <section className="bg-surface py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-background p-7 shadow-soft">
            <h2 className="accent-bar-diagonal font-display text-2xl font-bold text-secondary md:text-3xl">
              Qué incluye
            </h2>
            <ul className="mt-5 space-y-3">
              {v.includes.map((t) => (
                <li key={t.texto} className="flex items-start gap-3 text-secondary/85">
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-accent text-accent-foreground text-[10px] font-bold">
                    ✓
                  </span>
                  <TextoRico t={t} className={ESTILO_ENLACES} />
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-background p-7 shadow-soft">
            <h2 className="accent-bar-diagonal font-display text-2xl font-bold text-secondary md:text-3xl">
              Para quién aplica
            </h2>
            <ul className="mt-5 space-y-3">
              {v.audience.map((t) => (
                <li key={t.texto} className="flex items-start gap-3 text-secondary/85">
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground text-[10px] font-bold">
                    →
                  </span>
                  <TextoRico t={t} className={ESTILO_ENLACES} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TIPOS DE PROYECTOS */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Tipos de campañas
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Tipos de proyectos que ejecutamos
            </h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {v.projectTypes.map((t) => (
              <article
                key={t.title}
                className="rounded-lg border border-border bg-background p-6 shadow-soft"
              >
                <h3 className="font-display text-lg font-bold text-secondary">{t.title}</h3>
                <p className={`mt-2 text-sm text-secondary/70 ${ESTILO_ENLACES}`}>
                  <TextoRico t={t.desc} />
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="bg-secondary py-16 text-secondary-foreground md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
              · Beneficios
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
              Qué ganas al trabajar con nosotros
            </h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {v.benefits.map((b) => (
              <div key={b.title} className="rounded-lg border border-white/10 bg-white/5 p-6">
                <h3 className="font-display text-lg font-bold">{b.title}</h3>
                <p className={`mt-2 text-sm text-secondary-foreground/75 ${ESTILO_ENLACES}`}>
                  <TextoRico t={b.desc} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {p.gallery && p.gallery.length > 0 && <ServiceGallery items={p.gallery} />}

      {/* INLINE CTA — visible without reaching footer (US3) */}
      <section className="section-dark py-14 md:py-16">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <h2 className="font-display text-2xl font-bold text-ink-warm md:text-3xl">
            ¿Listo para cotizar {p.eyebrow}?
          </h2>
          <p className="mt-3 text-ink-warm/70">
            Te enviamos propuesta y presupuesto en menos de 72 horas.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/cotizar" className="btn-cta rounded-lg px-8 py-3.5 text-base font-semibold">
              Solicitar propuesta →
            </Link>
            <WhatsAppLink
              topic={serviceName}
              topicId={p.slug.slice(1)}
              className="inline-flex items-center gap-2 rounded-lg border border-ink-warm/40 px-6 py-3.5 text-sm font-semibold text-ink-warm hover:border-ink-warm"
            >
              Cotizar por WhatsApp
            </WhatsAppLink>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQAccordion
        items={v.faqs.map((f) => ({ q: f.q, a: f.a.texto, aHtml: f.a.html }))}
        title="Preguntas frecuentes"
      />

      {/* CTA FINAL + FORM */}
      <section className="py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Cotizar
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Cuéntanos tu proyecto. Te enviamos propuesta.
            </h2>
            <p className="mt-4 text-lg text-secondary/75">
              Completa los datos y te contactamos en menos de 24 horas hábiles para coordinar la
              propuesta. Sin compromiso.
            </p>
            <div className="mt-8">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-secondary">
                Otros servicios
              </h3>
              <ul className="mt-3 flex flex-wrap gap-2">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={r.slug}
                      className="inline-flex rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary"
                    >
                      {r.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/servicios"
                    className="inline-flex rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary"
                  >
                    Ver todos →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <Suspense fallback={<LeadFormSkeleton />}>
            <LeadForm defaultService={serviceName} />
          </Suspense>
        </div>
      </section>
    </div>
  );
}
